/** @format */

import {
  MutableRefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  ColumnSizingState,
  ExpandedState,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  OnChangeFn,
  Row,
  RowSelectionState,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';

import {MIN_COLUMN_WIDTH} from './constants';
import {ColumnType, SortDirection, TanStackTableData} from './types';
import {createColumns} from './helpers/createColumns';
import {filterData} from './helpers/filterData';
import {useColumnSizingStorage} from './hooks/useColumnSizingStorage';
import {useTableFilters} from './hooks/useTableFilters';

export interface UseTableModelParams {
  columns: ColumnType[];
  data: TanStackTableData[];
  search?: string;
  searchByColumnIds?: string[];
  defaultSortBy?: string;
  defaultOrderBy?: SortDirection;
  enableSorting?: boolean;
  enableFilters?: boolean;
  filterOrder?: string[];
  onOpenSubRows?: (value: boolean) => void;
  onFilterChange?: (columnId: string, value: string | null | undefined) => void;
  rowSelection?: RowSelectionState;
  setRowSelection?: OnChangeFn<RowSelectionState>;
  rowIdKey?: string;
  isRowSelectionDisabled?: (row: Row<TanStackTableData>) => boolean;
  exportDataRef?: MutableRefObject<TanStackTableData[]>;
  onExportDataChange?: (dataLength: number) => void;
  // Ключ localStorage для пользовательских ширин колонок; задан — ресайз включён.
  columnResizingKey?: string;
}

export const useTableModel = ({
  columns,
  data,
  search,
  searchByColumnIds,
  defaultSortBy,
  defaultOrderBy,
  onOpenSubRows,
  onFilterChange: onFilterChangeCallback,
  rowSelection,
  setRowSelection,
  enableSorting = true,
  enableFilters = false,
  filterOrder = [],
  rowIdKey = 'id',
  isRowSelectionDisabled,
  exportDataRef,
  onExportDataChange,
  columnResizingKey,
}: UseTableModelParams) => {
  const [sorting, setSorting] = useState<SortingState>(() =>
    defaultSortBy ? [{id: defaultSortBy, desc: defaultOrderBy === 'desc'}] : [],
  );
  const [expanded, setExpanded] = useState<ExpandedState>({});

  const {storedSizing, persistSizing, adoptStoredSizing} = useColumnSizingStorage({
    key: columnResizingKey,
  });
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(storedSizing);

  const isColumnResizingEnabled = Boolean(columnResizingKey);

  const setColumnSize = useCallback((columnId: string, width: number) => {
    setColumnSizing(prev => ({...prev, [columnId]: Math.max(MIN_COLUMN_WIDTH, width)}));
  }, []);

  const resetColumnSize = useCallback((columnId: string) => {
    setColumnSizing(({[columnId]: _removed, ...rest}) => rest);
  }, []);

  // ОДИН ref на пару {key, sizing} и ОДИН эффект — намеренно, а не два раздельных эффекта
  // (персист по columnSizing + resync по columnResizingKey). Причины:
  //
  // 1. Независимость от порядка объявления. Если бы это были два отдельных useEffect, порядок их
  //    объявления в файле становился бы частью контракта (при смене ключа эффект resync должен
  //    объявляться ПОСЛЕ эффекта персиста — иначе в том же коммите persist-эффект успевает
  //    отработать уже после того, как resync обновил свой ref, но ДО того, как columnSizing в
  //    стейте подхватил новое значение (React батчит setState из эффекта на следующий рендер) —
  //    ref и state на мгновение расходятся, guard ошибочно пропускает, и старый sizing пишется
  //    под новый ключ). Это хрупко и незаметно при рефакторинге.
  //
  // 2. Гонка в ОДНОМ коммите. Если columnResizingKey и columnSizing меняются в одном и том же
  //    коммите (React batching: например, родитель меняет ключ ровно тогда же, когда локальный
  //    драг ещё не зафиксирован), раздельный персист-эффект сравнивает columnSizing по ссылке и
  //    видит легитимное изменение (пользователь подвинул колонку) — он не в курсе, что ключ
  //    ТОЖЕ сменился в этом коммите, и пишет columnSizing старой таблицы уже под НОВЫЙ ключ
  //    (persistSizing к этому моменту уже пересобран на новый key). Проверка key ПЕРВОЙ, внутри
  //    одного эффекта, гарантирует: смена ключа всегда обрабатывается раньше и отдельно от
  //    персиста, отбрасывая ещё не сохранённый sizing прежней таблицы, а не перенося его.
  //
  // Обе гарантии проверены тестами: «дефект #7» (перечитывание при смене ключа в разных коммитах)
  // и «смена columnResizingKey и изменение columnSizing в одном коммите» (гонка внутри одного).
  const previousRef = useRef<{key: string | undefined; sizing: ColumnSizingState}>({
    key: columnResizingKey,
    sizing: columnSizing,
  });

  useEffect(() => {
    if (previousRef.current.key !== columnResizingKey) {
      // Ключ сменился — перечитываем сайзинг из-под нового ключа и не персистим то, что было
      // накоплено под старым (даже если оно ещё не записано в этом же коммите).
      const nextSizing = adoptStoredSizing();
      previousRef.current = {key: columnResizingKey, sizing: nextSizing};
      setColumnSizing(nextSizing);
      return;
    }
    // Пишем в localStorage только когда columnSizing реально изменился (сравнение по ссылке
    // с предыдущим значением), а не при каждом маунте — иначе любой маунт таблицы пишет текущее
    // состояние обратно (даже {} на пустых columns во время загрузки) и стирает то, что реально
    // сохранил пользователь.
    if (previousRef.current.sizing === columnSizing) {
      return;
    }
    previousRef.current = {key: columnResizingKey, sizing: columnSizing};
    persistSizing(columnSizing);
  }, [columnResizingKey, columnSizing, persistSizing, adoptStoredSizing]);

  const filterableColumnIds = useMemo(
    () => columns.filter(col => col.enableFilter).map(col => col.id),
    [columns],
  );

  const processedData = useMemo(() => {
    return search ? filterData({search, searchByColumnIds, data}) : data;
  }, [search, searchByColumnIds, data]);

  const {
    filters,
    filteredData,
    filterOptions,
    handleFilterChange: onTableFilterChange,
    handleResetFilters,
  } = useTableFilters({
    data: processedData,
    filterableColumns: filterableColumnIds,
    filterOrder,
  });

  const handleFilterChange = useCallback(
    (columnId: string, value: string | null | undefined) => {
      onTableFilterChange(columnId, value);
      onFilterChangeCallback?.(columnId, value);
    },
    [onTableFilterChange, onFilterChangeCallback],
  );

  const finalData = useMemo(
    () => (enableFilters ? filteredData : processedData),
    [enableFilters, processedData, filteredData],
  );

  const enableRowSelection = useMemo(
    () => Boolean(rowSelection && setRowSelection),
    [rowSelection, setRowSelection],
  );

  useLayoutEffect(() => {
    onOpenSubRows?.(Object.keys(expanded).length > 0);
  }, [expanded, onOpenSubRows]);

  const tanStackColumns = useMemo(() => createColumns(columns), [columns]);

  const table = useReactTable({
    data: finalData,
    columns: tanStackColumns,
    state: {
      expanded,
      sorting,
      rowSelection,
      columnSizing,
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getSubRows: row => row.subRows,
    onExpandedChange: setExpanded,
    onSortingChange: setSorting,
    onColumnSizingChange: setColumnSizing,
    enableColumnResizing: isColumnResizingEnabled,
    onRowSelectionChange: setRowSelection,
    getRowId: (originalRow, index, parent) => {
      // Index signature types these as non-nullable, but rows may lack the key at runtime — keep the fallback chain.
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      const val = originalRow[rowIdKey] ?? originalRow.id ?? originalRow.ID;
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      return val != null ? String(val) : parent ? `${parent.id}.${index}` : String(index);
    },
    enableSortingRemoval: false,
    enableMultiSort: false,
    enableSorting,
    enableRowSelection,
  });

  const isWithSubRows = data.some(row => row.subRows?.length);

  const {rows} = table.getRowModel();

  useLayoutEffect(() => {
    if (exportDataRef) {
      exportDataRef.current = rows.map(r => r.original);
    }
  }, [exportDataRef, rows]);

  useEffect(() => {
    onExportDataChange?.(rows.length);
  }, [rows.length, onExportDataChange]);

  const isAllSelectionDisabled = useMemo(() => {
    if (!isRowSelectionDisabled) return false;
    return rows.every(row => isRowSelectionDisabled(row) || row.getIsSelected());
  }, [isRowSelectionDisabled, rows]);

  const headerGroups = table.getHeaderGroups();
  const allSubColumnIds = headerGroups.flatMap(g =>
    g.headers.flatMap(h => h.column.columns.map(c => c.id)),
  );

  return {
    table,
    rows,
    headerGroups,
    allSubColumnIds,
    isWithSubRows,
    enableRowSelection,
    isAllSelectionDisabled,
    filters,
    filterOptions,
    handleFilterChange,
    handleResetFilters,
    isColumnResizingEnabled,
    setColumnSize,
    resetColumnSize,
  };
};
